import customCampaignTargetManager from "@shared/common/custom-target-condition";
import {
  ICampaignTargetCustom,
  TargetCustomCreationInfo,
} from "@shared/common/custom-target-condition/types";
import { deepclone } from "@shared/common/node/util";
import {
  CampaignTargetType,
  CampaignTargetTypeOnlyField,
  ICampaignTargetCookie,
  ICampaignTargetGeolocation,
  ICampaignTargetHTML,
  ICampaignTargetIp,
  ICampaignTargetIsFirst,
  ICampaignTargetLanguage,
  ICampaignTargetLoc,
  ICampaignTargetLocalStorage,
  ICampaignTargetNewOrReturningVisitor,
  ICampaignTargetOS,
  ICampaignTargetPage,
  ICampaignTargetQueryString,
  ICampaignTargetReferrer,
  ICampaignTargetSessionStorage,
  ICampaignTargetUserAgent,
  ICampaignTargetUtm,
  ICampaignTargetWindow,
} from "@shared/common/types/campaign";
import { getDefaultTargetRoot, getTargetVersion } from "./common";

export const latestVersion = "7";

export type OrGroup<
  T extends CampaignTargetType,
  Name extends string = CampaignTargetType["type"],
> = {
  type: "OR";
  value: T[];
  name?: Name;
};

export type AndGroup<
  T extends CampaignTargetType,
  Name extends string = CampaignTargetType["type"],
> = {
  type: "AND";
  value: T[];
  name?: Name;
};

export type TargetByVersion7 = {
  type: "ROOT";
  version: "7";
  value: {
    type: "AND";
    name?: "ROOT";
    value: (
      | OrGroup<ICampaignTargetLoc, "DOMAIN_INCLUDED">
      | AndGroup<ICampaignTargetLoc, "DOMAIN_NOT_INCLUDED">
      | OrGroup<
          ICampaignTargetPage | ICampaignTargetLoc,
          "PAGE_AND_URL_INCLUDED"
        >
      | AndGroup<ICampaignTargetLoc, "PAGE_AND_URL_NOT_INCLUDED">
      | OrGroup<ICampaignTargetUserAgent, "USER_AGENT_INCLUDED">
      | AndGroup<ICampaignTargetUserAgent, "USER_AGENT_NOT_INCLUDED">
      | OrGroup<ICampaignTargetReferrer, "REFERRER_INCLUDED">
      | AndGroup<ICampaignTargetReferrer, "REFERRER_NOT_INCLUDED">
      | OrGroup<ICampaignTargetLanguage, "LANGUAGE_INCLUDED">
      | AndGroup<ICampaignTargetLanguage, "LANGUAGE_NOT_INCLUDED">
      | OrGroup<ICampaignTargetUtm, "UTM_INCLUDED">
      | AndGroup<ICampaignTargetUtm, "UTM_NOT_INCLUDED">
      | OrGroup<ICampaignTargetGeolocation, "GEOLOCATION_INCLUDED">
      | AndGroup<ICampaignTargetGeolocation, "GEOLOCATION_NOT_INCLUDED">
      | OrGroup<ICampaignTargetLocalStorage, "LOCALSTORAGE_INCLUDED">
      | AndGroup<ICampaignTargetLocalStorage, "LOCALSTORAGE_NOT_INCLUDED">
      | OrGroup<ICampaignTargetSessionStorage, "SESSIONSTORAGE_INCLUDED">
      | AndGroup<ICampaignTargetSessionStorage, "SESSIONSTORAGE_NOT_INCLUDED">
      | OrGroup<ICampaignTargetCookie, "COOKIE_INCLUDED">
      | AndGroup<ICampaignTargetCookie, "COOKIE_NOT_INCLUDED">
      | OrGroup<ICampaignTargetQueryString, "QUERY_STRING_INCLUDED">
      | AndGroup<ICampaignTargetQueryString, "QUERY_STRING_NOT_INCLUDED">
      | OrGroup<ICampaignTargetIp, "IP_INCLUDED">
      | AndGroup<ICampaignTargetIp, "IP_NOT_INCLUDED">
      | OrGroup<ICampaignTargetHTML, "HTML_INCLUDED">
      | AndGroup<ICampaignTargetHTML, "HTML_NOT_INCLUDED">
      | OrGroup<ICampaignTargetIsFirst, "IS_FIRST_INCLUDED">
      | AndGroup<ICampaignTargetIsFirst, "IS_FIRST_NOT_INCLUDED">
      | OrGroup<ICampaignTargetWindow, "WINDOW_INCLUDED">
      | AndGroup<ICampaignTargetWindow, "WINDOW_NOT_INCLUDED">
      | OrGroup<ICampaignTargetOS>
      | ICampaignTargetCustom
      | OrGroup<
          ICampaignTargetNewOrReturningVisitor,
          "NEW_OR_RETURNING_VISITOR_INCLUDED"
        >
      | AndGroup<
          ICampaignTargetNewOrReturningVisitor,
          "NEW_OR_RETURNING_VISITOR_NOT_INCLUDED"
        >
    )[];
  };
};
export const getAdditionalTargetValues = () => {
  const domainNotIncludedItem = {
    type: "AND",
    value: [],
    name: "DOMAIN_NOT_INCLUDED",
  } as AndGroup<ICampaignTargetLoc, "DOMAIN_NOT_INCLUDED">;
  const pageAndUrlNotIncludedItem = {
    type: "AND",
    value: [],
    name: "PAGE_AND_URL_NOT_INCLUDED",
  } as AndGroup<ICampaignTargetLoc, "PAGE_AND_URL_NOT_INCLUDED">;
  const userAgentNotIncludedItem = {
    type: "AND",
    value: [],
    name: "USER_AGENT_NOT_INCLUDED",
  } as AndGroup<ICampaignTargetUserAgent, "USER_AGENT_NOT_INCLUDED">;
  const domainIncludedItem = {
    type: "OR",
    value: [],
    name: "DOMAIN_INCLUDED",
  } as OrGroup<ICampaignTargetLoc, "DOMAIN_INCLUDED">;
  const pageAndUrlIncludedItem = {
    type: "OR",
    value: [],
    name: "PAGE_AND_URL_INCLUDED",
  } as OrGroup<ICampaignTargetLoc, "PAGE_AND_URL_INCLUDED">;
  const userAgentIncludedItem = {
    type: "OR",
    value: [],
    name: "USER_AGENT_INCLUDED",
  } as OrGroup<ICampaignTargetUserAgent, "USER_AGENT_INCLUDED">;
  const referrerIncludedItem = {
    type: "OR",
    value: [],
    name: "REFERRER_INCLUDED",
  } as OrGroup<ICampaignTargetReferrer, "REFERRER_INCLUDED">;

  const referrerNotIncludedItem = {
    type: "AND",
    value: [],
    name: "REFERRER_NOT_INCLUDED",
  } as AndGroup<ICampaignTargetReferrer, "REFERRER_NOT_INCLUDED">;

  const languageIncludedItem = {
    type: "OR",
    value: [],
    name: "LANGUAGE_INCLUDED",
  } as OrGroup<ICampaignTargetLanguage, "LANGUAGE_INCLUDED">;

  const languageNotIncludedItem = {
    type: "AND",
    value: [],
    name: "LANGUAGE_NOT_INCLUDED",
  } as AndGroup<ICampaignTargetLanguage, "LANGUAGE_NOT_INCLUDED">;

  const utmIncludedItem = {
    type: "OR",
    value: [],
    name: "UTM_INCLUDED",
  } as OrGroup<ICampaignTargetUtm, "UTM_INCLUDED">;

  const utmNotIncludedItem = {
    type: "AND",
    value: [],
    name: "UTM_NOT_INCLUDED",
  } as AndGroup<ICampaignTargetUtm, "UTM_NOT_INCLUDED">;

  const geolocationIncludedItem = {
    type: "OR",
    value: [],
    name: "GEOLOCATION_INCLUDED",
  } as OrGroup<ICampaignTargetGeolocation, "GEOLOCATION_INCLUDED">;

  const geolocationNotIncludedItem = {
    type: "AND",
    value: [],
    name: "GEOLOCATION_NOT_INCLUDED",
  } as AndGroup<ICampaignTargetGeolocation, "GEOLOCATION_NOT_INCLUDED">;

  const localStorageIncludedItem = {
    type: "OR",
    value: [],
    name: "LOCALSTORAGE_INCLUDED",
  } as OrGroup<ICampaignTargetLocalStorage, "LOCALSTORAGE_INCLUDED">;

  const localStorageNotIncludedItem = {
    type: "AND",
    value: [],
    name: "LOCALSTORAGE_NOT_INCLUDED",
  } as AndGroup<ICampaignTargetLocalStorage, "LOCALSTORAGE_NOT_INCLUDED">;

  const sessionStorageIncludedItem = {
    type: "OR",
    value: [],
    name: "SESSIONSTORAGE_INCLUDED",
  } as OrGroup<ICampaignTargetSessionStorage, "SESSIONSTORAGE_INCLUDED">;

  const sessionStorageNotIncludedItem = {
    type: "AND",
    value: [],
    name: "SESSIONSTORAGE_NOT_INCLUDED",
  } as AndGroup<ICampaignTargetSessionStorage, "SESSIONSTORAGE_NOT_INCLUDED">;

  const cookieIncludedItem = {
    type: "OR",
    value: [],
    name: "COOKIE_INCLUDED",
  } as OrGroup<ICampaignTargetCookie, "COOKIE_INCLUDED">;

  const cookieNotIncludedItem = {
    type: "AND",
    value: [],
    name: "COOKIE_NOT_INCLUDED",
  } as AndGroup<ICampaignTargetCookie, "COOKIE_NOT_INCLUDED">;

  const queryStringIncludedItem = {
    type: "OR",
    value: [],
    name: "QUERY_STRING_INCLUDED",
  } as OrGroup<ICampaignTargetQueryString, "QUERY_STRING_INCLUDED">;

  const queryStringNotIncludedItem = {
    type: "AND",
    value: [],
    name: "QUERY_STRING_NOT_INCLUDED",
  } as AndGroup<ICampaignTargetQueryString, "QUERY_STRING_NOT_INCLUDED">;

  const ipIncludedItem = {
    type: "OR",
    value: [],
    name: "IP_INCLUDED",
  } as OrGroup<ICampaignTargetIp, "IP_INCLUDED">;

  const ipNotIncludedItem = {
    type: "AND",
    value: [],
    name: "IP_NOT_INCLUDED",
  } as AndGroup<ICampaignTargetIp, "IP_NOT_INCLUDED">;

  const htmlIncludedItem = {
    type: "OR",
    value: [],
    name: "HTML_INCLUDED",
  } as OrGroup<ICampaignTargetHTML, "HTML_INCLUDED">;

  const htmlNotIncludedItem = {
    type: "AND",
    value: [],
    name: "HTML_NOT_INCLUDED",
  } as AndGroup<ICampaignTargetHTML, "HTML_NOT_INCLUDED">;

  const isFirstIncludedItem = {
    type: "OR",
    value: [],
    name: "IS_FIRST_INCLUDED",
  } as OrGroup<ICampaignTargetIsFirst, "IS_FIRST_INCLUDED">;

  const isFirstNotIncludedItem = {
    type: "AND",
    value: [],
    name: "IS_FIRST_NOT_INCLUDED",
  } as AndGroup<ICampaignTargetIsFirst, "IS_FIRST_NOT_INCLUDED">;

  const windowIncludedItem = {
    type: "OR",
    value: [],
    name: "WINDOW_INCLUDED",
  } as OrGroup<ICampaignTargetWindow, "WINDOW_INCLUDED">;

  const windowNotIncludedItem = {
    type: "AND",
    value: [],
    name: "WINDOW_NOT_INCLUDED",
  } as AndGroup<ICampaignTargetWindow, "WINDOW_NOT_INCLUDED">;

  const newOrReturningVisitorIncludedItem = {
    type: "OR",
    value: [],
    name: "NEW_OR_RETURNING_VISITOR_INCLUDED",
  } as OrGroup<
    ICampaignTargetNewOrReturningVisitor,
    "NEW_OR_RETURNING_VISITOR_INCLUDED"
  >;

  const newOrReturningVisitorNotIncludedItem = {
    type: "AND",
    value: [],
    name: "NEW_OR_RETURNING_VISITOR_NOT_INCLUDED",
  } as AndGroup<
    ICampaignTargetNewOrReturningVisitor,
    "NEW_OR_RETURNING_VISITOR_NOT_INCLUDED"
  >;

  const newAndGroupItems = [
    domainNotIncludedItem,
    pageAndUrlNotIncludedItem,
    userAgentNotIncludedItem,
    referrerNotIncludedItem,
    languageNotIncludedItem,
    utmNotIncludedItem,
    geolocationNotIncludedItem,
    localStorageNotIncludedItem,
    sessionStorageNotIncludedItem,
    cookieNotIncludedItem,
    queryStringNotIncludedItem,
    ipNotIncludedItem,
    htmlNotIncludedItem,
    isFirstNotIncludedItem,
    windowNotIncludedItem,
    newOrReturningVisitorNotIncludedItem,
  ];

  const newOrGroupItems = [
    domainIncludedItem,
    pageAndUrlIncludedItem,
    userAgentIncludedItem,
    referrerIncludedItem,
    languageIncludedItem,
    utmIncludedItem,
    geolocationIncludedItem,
    localStorageIncludedItem,
    sessionStorageIncludedItem,
    cookieIncludedItem,
    queryStringIncludedItem,
    ipIncludedItem,
    htmlIncludedItem,
    isFirstIncludedItem,
    windowIncludedItem,
    newOrReturningVisitorIncludedItem,
  ];

  return { newAndGroupItems, newOrGroupItems };
};

export type TargetByVersion = {
  "1": {
    type: "OR";
    value: CampaignTargetType[];
  };
  "2": {
    type: "ROOT";
    version: "2";
    value: {
      type: "AND";
      value: (
        | {
            type: "OR";
            value: CampaignTargetType[];
          }
        | {
            type: "USER_AGENT";
            value: "";
            operator: "INCLUDE";
          }
        | ICampaignTargetCustom
      )[];
    };
  };
  "3": {
    type: "ROOT";
    version: "3";
    value: {
      type: "AND";
      value: (
        | OrGroup<ICampaignTargetPage | ICampaignTargetLoc, "PAGE_AND_URL">
        | OrGroup<ICampaignTargetUserAgent>
        | OrGroup<ICampaignTargetOS>
        | ICampaignTargetCustom
      )[];
    };
  };
  "4": {
    type: "ROOT";
    version: "4";
    value: {
      type: "AND";
      name?: "ROOT";
      value: (
        | OrGroup<ICampaignTargetLoc, "DOMAIN">
        | OrGroup<ICampaignTargetPage | ICampaignTargetLoc, "PAGE_AND_URL">
        | OrGroup<ICampaignTargetUserAgent>
        | OrGroup<ICampaignTargetOS>
        | ICampaignTargetCustom
      )[];
    };
  };
  "5": {
    type: "ROOT";
    version: "5";
    value: {
      type: "AND";
      name?: "ROOT";
      value: (
        | OrGroup<ICampaignTargetLoc, "DOMAIN_INCLUDED">
        | OrGroup<ICampaignTargetLoc, "DOMAIN_NOT_INCLUDED">
        | OrGroup<
            ICampaignTargetPage | ICampaignTargetLoc,
            "PAGE_AND_URL_INCLUDED"
          >
        | OrGroup<ICampaignTargetLoc, "PAGE_AND_URL_NOT_INCLUDED">
        | OrGroup<ICampaignTargetUserAgent, "USER_AGENT_INCLUDED">
        | OrGroup<ICampaignTargetUserAgent, "USER_AGENT_NOT_INCLUDED">
        | OrGroup<ICampaignTargetOS>
        | ICampaignTargetCustom
      )[];
    };
  };
  "6": {
    type: "ROOT";
    version: "6";
    value: {
      type: "AND";
      name?: "ROOT";
      value: (
        | OrGroup<ICampaignTargetLoc, "DOMAIN_INCLUDED">
        | AndGroup<ICampaignTargetLoc, "DOMAIN_NOT_INCLUDED">
        | OrGroup<
            ICampaignTargetPage | ICampaignTargetLoc,
            "PAGE_AND_URL_INCLUDED"
          >
        | AndGroup<ICampaignTargetLoc, "PAGE_AND_URL_NOT_INCLUDED">
        | OrGroup<ICampaignTargetUserAgent, "USER_AGENT_INCLUDED">
        | AndGroup<ICampaignTargetUserAgent, "USER_AGENT_NOT_INCLUDED">
        | OrGroup<ICampaignTargetReferrer, "REFERRER_INCLUDED">
        | AndGroup<ICampaignTargetReferrer, "REFERRER_NOT_INCLUDED">
        | OrGroup<ICampaignTargetLanguage, "LANGUAGE_INCLUDED">
        | AndGroup<ICampaignTargetLanguage, "LANGUAGE_NOT_INCLUDED">
        | OrGroup<ICampaignTargetUtm, "UTM_INCLUDED">
        | AndGroup<ICampaignTargetUtm, "UTM_NOT_INCLUDED">
        | OrGroup<ICampaignTargetGeolocation, "GEOLOCATION_INCLUDED">
        | AndGroup<ICampaignTargetGeolocation, "GEOLOCATION_NOT_INCLUDED">
        | OrGroup<ICampaignTargetLocalStorage, "LOCALSTORAGE_INCLUDED">
        | AndGroup<ICampaignTargetLocalStorage, "LOCALSTORAGE_NOT_INCLUDED">
        | OrGroup<ICampaignTargetSessionStorage, "SESSIONSTORAGE_INCLUDED">
        | AndGroup<ICampaignTargetSessionStorage, "SESSIONSTORAGE_NOT_INCLUDED">
        | OrGroup<ICampaignTargetCookie, "COOKIE_INCLUDED">
        | AndGroup<ICampaignTargetCookie, "COOKIE_NOT_INCLUDED">
        | OrGroup<ICampaignTargetQueryString, "QUERY_STRING_INCLUDED">
        | AndGroup<ICampaignTargetQueryString, "QUERY_STRING_NOT_INCLUDED">
        | OrGroup<ICampaignTargetIp, "IP_INCLUDED">
        | AndGroup<ICampaignTargetIp, "IP_NOT_INCLUDED">
        | OrGroup<ICampaignTargetHTML, "HTML_INCLUDED">
        | AndGroup<ICampaignTargetHTML, "HTML_NOT_INCLUDED">
        | OrGroup<ICampaignTargetIsFirst, "IS_FIRST_INCLUDED">
        | AndGroup<ICampaignTargetIsFirst, "IS_FIRST_NOT_INCLUDED">
        | OrGroup<ICampaignTargetWindow, "WINDOW_INCLUDED">
        | AndGroup<ICampaignTargetWindow, "WINDOW_NOT_INCLUDED">
        | OrGroup<ICampaignTargetOS>
        | ICampaignTargetCustom
      )[];
    };
  };
  "7": TargetByVersion7;
};

export type GeneralTargetConverter = (
  target: CampaignTargetType,
  info?: TargetCustomCreationInfo
) => CampaignTargetType;

export const convertToNextVersion = [
  (
    source: TargetByVersion["1"],
    info?: TargetCustomCreationInfo
  ): TargetByVersion["2"] => {
    return {
      type: "ROOT",
      version: "2",
      value: {
        type: "AND",
        value: [
          source,
          { type: "USER_AGENT", value: "", operator: "INCLUDE" },
          ...(info
            ? customCampaignTargetManager.getDefaultValueList(info)
            : []),
        ],
      },
    };
  },
  (
    source: TargetByVersion["2"],
    _info?: TargetCustomCreationInfo
  ): TargetByVersion["3"] => {
    const pages = deepclone(
      source.value.value.find(
        (item) =>
          item.type === "OR" &&
          (item.value[0]?.type === "PAGE" || item.value[0]?.type === "URL")
      ) as
        | OrGroup<ICampaignTargetPage | ICampaignTargetLoc, "PAGE_AND_URL">
        | undefined
    );

    const items: TargetByVersion["3"]["value"]["value"] = [];

    const userAgent = source.value.value.find(
      (item) => item.type === "USER_AGENT"
    ) as ICampaignTargetUserAgent;

    if (pages) {
      pages.name = "PAGE_AND_URL";
      items.push(pages);
    } else {
      items.push({
        type: "OR",
        value: [
          {
            type: "PAGE",
            value: "HOME",
          },
        ],
        name: "PAGE_AND_URL",
      });
    }

    items.push({
      type: "OR",
      value: userAgent && userAgent.value !== "" ? [userAgent] : [],
      name: "USER_AGENT",
    });

    items.push({
      type: "OR",
      value: [],
      name: "OS",
    });

    items.push(
      ...(source.value.value.filter(
        (item) => item.type === "CUSTOM"
      ) as ICampaignTargetCustom[])
    );

    return {
      type: "ROOT",
      version: "3",
      value: {
        type: "AND",
        value: items,
      },
    };
  },
  (
    source: TargetByVersion["3"],
    _info?: TargetCustomCreationInfo
  ): TargetByVersion["4"] => {
    const result: TargetByVersion["4"] = {
      ...deepclone(source),
      version: "4",
    };

    result.value.value.push({
      type: "OR",
      name: "DOMAIN",
      value: [],
    });

    return result;
  },
  (
    source: TargetByVersion["4"],
    _info?: TargetCustomCreationInfo
  ): TargetByVersion["5"] => {
    const domainIncludedItem = {
      type: "OR",
      value: [],
      ...deepclone(
        source.value.value.find(
          (target) => target.type === "OR" && target.name === "DOMAIN"
        )
      ),
      name: "DOMAIN_INCLUDED",
    } as OrGroup<ICampaignTargetLoc, "DOMAIN_INCLUDED">;

    const domainNotIncludedItem = {
      type: "OR",
      value: [],
      name: "DOMAIN_NOT_INCLUDED",
    } as OrGroup<ICampaignTargetLoc, "DOMAIN_NOT_INCLUDED">;

    const pageAndUrlIncludedItem = {
      type: "OR",
      value: [
        {
          type: "PAGE",
          value: "HOME",
        },
      ],
      ...deepclone(
        source.value.value.find(
          (target) => target.type === "OR" && target.name === "PAGE_AND_URL"
        )
      ),
      name: "PAGE_AND_URL_INCLUDED",
    } as OrGroup<
      ICampaignTargetPage | ICampaignTargetLoc,
      "PAGE_AND_URL_INCLUDED"
    >;

    const pageAndUrlNotIncludedItem = {
      type: "OR",
      value: [],
      name: "PAGE_AND_URL_NOT_INCLUDED",
    } as OrGroup<ICampaignTargetLoc, "PAGE_AND_URL_NOT_INCLUDED">;

    const userAgentIncludedItem = {
      type: "OR",
      value: [],
      name: "USER_AGENT_INCLUDED",
    } as OrGroup<ICampaignTargetUserAgent, "USER_AGENT_INCLUDED">;

    const userAgentNotIncludedItem = {
      type: "OR",
      value: [],
      name: "USER_AGENT_NOT_INCLUDED",
    } as OrGroup<ICampaignTargetUserAgent, "USER_AGENT_NOT_INCLUDED">;

    const osItem = {
      type: "OR",
      value: [],
      name: "OS",
    } as OrGroup<ICampaignTargetOS>;

    const customItems = source.value.value.filter(
      (item) => item.type === "CUSTOM"
    ) as ICampaignTargetCustom[];

    return {
      type: "ROOT",
      version: "5",
      value: {
        type: "AND",
        value: [
          domainIncludedItem,
          domainNotIncludedItem,
          pageAndUrlIncludedItem,
          pageAndUrlNotIncludedItem,
          userAgentIncludedItem,
          userAgentNotIncludedItem,
          osItem,
          ...customItems,
        ],
      },
    };
  },
  (
    source: TargetByVersion["5"],
    _info?: TargetCustomCreationInfo
  ): TargetByVersion["6"] => {
    const v5Items = deepclone(source);

    const willChangeToAndGroup = (
      target: TargetByVersion["5"]["value"]["value"][number]
    ): target is
      | OrGroup<ICampaignTargetLoc, "DOMAIN_NOT_INCLUDED">
      | OrGroup<ICampaignTargetLoc, "PAGE_AND_URL_NOT_INCLUDED">
      | OrGroup<ICampaignTargetUserAgent, "USER_AGENT_INCLUDED"> =>
      target.type === "OR" &&
      (target.name === "DOMAIN_NOT_INCLUDED" ||
        target.name === "PAGE_AND_URL_NOT_INCLUDED" ||
        target.name === "USER_AGENT_NOT_INCLUDED");

    const andGroupItems = v5Items.value.value
      .filter(willChangeToAndGroup)
      .map((filteredTarget) => ({
        ...filteredTarget,
        type: "AND",
      }));

    const orGroupItems = v5Items.value.value.filter(
      (target) => !willChangeToAndGroup(target)
    );

    return {
      type: "ROOT",
      version: "6",
      value: {
        type: "AND",
        value: [...andGroupItems, ...orGroupItems],
      },
    } as TargetByVersion["6"];
  },
  (
    source: TargetByVersion["6"],
    _info?: TargetCustomCreationInfo
  ): TargetByVersion["7"] => {
    const v6Items = deepclone(source);

    return {
      type: "ROOT",
      version: "7",
      value: {
        type: "AND",
        value: v6Items.value.value,
      },
    } as TargetByVersion["7"];
  },
];

export const setAllTargetFields = (source: CampaignTargetType) => {
  const additionalTargetValues = getAdditionalTargetValues();
  const allTargetFields = [
    ...additionalTargetValues.newAndGroupItems,
    ...additionalTargetValues.newOrGroupItems,
  ];

  const newSource = deepclone(source) as TargetByVersion7;

  allTargetFields.forEach((item) => {
    if (
      !newSource.value.value.some((target) => {
        const nameTarget = target as CampaignTargetTypeOnlyField;
        return nameTarget.name === item.name;
      })
    ) {
      newSource.value.value.push(item);
    }
  });

  return newSource;
};

const getFieldsName = (fields: CampaignTargetTypeOnlyField[]) => {
  const emptyValue: { name: string; value: CampaignTargetTypeOnlyField }[] = [];

  fields.forEach((item) => {
    const name = item.name?.startsWith("DOMAIN")
      ? "editor:segment.target.panel.domain.name"
      : item.name?.startsWith("PAGE_AND_URL")
      ? "editor:segment.target.panel.page.name"
      : item.name?.startsWith("USER_AGENT")
      ? "editor:segment.target.panel.userAgent.name"
      : item.name?.startsWith("REFERRER")
      ? "editor:segment.target.panel.referrer.name"
      : item.name?.startsWith("LANGUAGE")
      ? "editor:segment.target.panel.browserLanguage.name"
      : item.name?.startsWith("UTM")
      ? "editor:segment.target.panel.utmSource.name"
      : item.name?.startsWith("LOCALSTORAGE")
      ? "editor:segment.target.panel.localStorage.name"
      : item.name?.startsWith("SESSIONSTORAGE")
      ? "editor:segment.target.panel.sessionStorage.name"
      : item.name?.startsWith("COOKIE")
      ? "editor:segment.target.panel.cookie.name"
      : item.name?.startsWith("QUERY_STRING")
      ? "editor:segment.target.panel.queryStrings.name"
      : item.name?.startsWith("HTML")
      ? "editor:segment.target.panel.html.name"
      : item.name?.startsWith("GEOLOCATION")
      ? "editor:segment.target.panel.geoLocation.name"
      : item.name?.startsWith("IP")
      ? "editor:segment.target.panel.ipAddress.name"
      : item.name?.startsWith("WINDOW")
      ? "editor:segment.target.panel.windowObject.name"
      : item.name?.startsWith("NEW_OR_RETURNING_VISITOR")
      ? "editor:segment.target.panel.newOrReturningVisitor.name"
      : "";

    emptyValue.push({
      value: item,
      name,
    });
  });

  return emptyValue;
};

export const getPositiveTargetValue = () => {
  const additionalTargetValues = getAdditionalTargetValues();

  const positiveTargetFields = [...additionalTargetValues.newOrGroupItems];

  return getFieldsName(positiveTargetFields).filter(
    (value) => value.name !== ""
  );
};
export const getNegativeTargetValue = () => {
  const additionalTargetValues = getAdditionalTargetValues();

  const negativeTargetFields = [...additionalTargetValues.newAndGroupItems];

  return getFieldsName(negativeTargetFields).filter(
    (value) => value.name !== ""
  );
};

export const convertToLatest = (
  source: CampaignTargetType,
  info?: TargetCustomCreationInfo
): CampaignTargetType => {
  if (getTargetVersion(source) === "INVALID") {
    throw new Error("Wrong version: INVALID");
  }
  let converted = source;
  while (getTargetVersion(converted) !== latestVersion) {
    const versionNumber = Number.parseInt(getTargetVersion(converted), 10);
    const convert = convertToNextVersion[versionNumber - 1] as
      | GeneralTargetConverter
      | undefined;

    if (!convert) {
      // eslint-disable-next-line no-console
      console.error(`no converter! ${versionNumber} to ${versionNumber + 1}`);
      return getDefaultTargetRoot(info);
    }

    converted = convert(converted, info);
  }

  return setAllTargetFields(converted);
};

export const removeEmptyTargetFields = (source: CampaignTargetType) => {
  const newSource = deepclone(source) as TargetByVersion7;

  newSource.value.value = newSource.value.value.filter((item) => {
    if (item.value.length > 0) {
      return true;
    }
    return false;
  });

  return newSource;
};
